import {
  Box,
  CircularProgress,
  MRT_PaginationState,
} from '@ouster/webviz-react-components';

import { Alert } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import RecordingManagementConsole from '../../components/adminDashboard/RecordingManagementConsole';
import RecordingManagementStaleList from '../../components/adminDashboard/RecordingManagementStaleList';
import { useStaleRecordings } from '../../components/adminDashboard/useAdminDashboard';
import { useStoreActions, useStoreState } from '../../reducers';

export const createAlert = (
  severity: 'info' | 'success' | 'error',
  message: string,
  includeTimestamp = false
) => {
  const timestamp = includeTimestamp ? ` (${new Date().toLocaleString()})` : '';

  return (
    <Alert
      severity={severity}
      sx={{ fontSize: '1.25rem', alignItems: 'center' }}
      variant="outlined"
    >
      {message + timestamp}
    </Alert>
  );
};

const HW_VERSION_UPDATE_BATCH_SIZE = 300;
const BATCH_PROCESSING_DELAY_MS = 200; // Delay between batch requests

export default function DevToolRecordingsMgmt() {
  const {
    updateHwVersionBatch,
    setHwVersionUpdateProgress,
    resetHwVersionProgress,
    reloadSensorMapping,
  } = useStoreActions((actions) => actions.adminDashboard);

  const { hwVersionUpdateProgress } = useStoreState(
    (state) => state.adminDashboard
  );

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const [enabled, setEnabled] = useState(false);
  const [reprocessedIds, setReprocessedIds] = useState<Set<string>>(new Set());
  const [osfStatus, setOsfStatus] = useState<'NOT_STARTED' | 'FAILED'>(
    'NOT_STARTED'
  );
  const [isUpdatingHWVersion, setIsUpdatingHWVersion] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<JSX.Element | null>(
    null
  );

  const { data, totalCount, isRefetching, isLoading, refetch } =
    useStaleRecordings(
      pagination.pageIndex + 1,
      pagination.pageSize,
      osfStatus,
      enabled
    );

  const handleFetchStale = () => {
    setIsUpdatingHWVersion(false);
    setEnabled(true);
    setOsfStatus('NOT_STARTED');
    refetch();
  };
  const handleFetchFailed = () => {
    setEnabled(true);
    setIsUpdatingHWVersion(false);
    setOsfStatus('FAILED');
    refetch();
  };

  const handleRefresh = () => {
    setReprocessedIds(new Set()); // Clear reprocessed IDs on refresh
    refetch();
  };

  const handleLoadHWMapping = async () => {
    try {
      const response = await reloadSensorMapping();
      setFeedbackMessage(
        createAlert(
          'success',
          `Sensor mapping reloaded: ${response.message}`,
          true
        )
      );
    } catch (error) {
      setFeedbackMessage(
        createAlert('error', `Failed to reload sensor mapping: ${error}`, true)
      );
    }
  };

  useEffect(() => {
    let isCancelled = false;
    let isProcessing = false;

    const processBatch = async () => {
      if (!hwVersionUpdateProgress.isInProgress || isProcessing) {
        return;
      }
      isProcessing = true;

      try {
        const result = await updateHwVersionBatch({
          page: hwVersionUpdateProgress.currentPage,
          perPage: HW_VERSION_UPDATE_BATCH_SIZE,
          lastProcessedId: hwVersionUpdateProgress.lastProcessedId,
        });

        if (isCancelled) return;

        const newTotalUpdated =
          hwVersionUpdateProgress.totalUpdated + result.updated;

        if (result.completed) {
          // Process completed
          const alertElement =
            newTotalUpdated === 0
              ? createAlert('info', 'No drives needed updating.', true)
              : createAlert(
                  'success',
                  `${newTotalUpdated} drives updated successfully.`,
                  true
                );
          setFeedbackMessage(alertElement);
          resetHwVersionProgress();
          setIsUpdatingHWVersion(false);
        } else {
          // Continue to next page
          setTimeout(() => {
            if (!isCancelled) {
              setHwVersionUpdateProgress({
                totalUpdated: newTotalUpdated,
                currentPage: hwVersionUpdateProgress.currentPage + 1,
                isInProgress: true,
                progressPercentage: result.progress_percentage || 0,
                lastProcessedId: result.last_processed_id,
              });
            }
          }, BATCH_PROCESSING_DELAY_MS); // Small delay between requests
        }
      } catch (error) {
        if (!isCancelled) {
          setFeedbackMessage(
            createAlert(
              'error',
              `Failed to update hardware version. ${error}`,
              true
            )
          );
          resetHwVersionProgress();
          setIsUpdatingHWVersion(false);
        }
      }
    };

    processBatch();

    return () => {
      isCancelled = true;
    };
  }, [
    hwVersionUpdateProgress.isInProgress,
    hwVersionUpdateProgress.currentPage,
    hwVersionUpdateProgress.totalUpdated,
    hwVersionUpdateProgress.lastProcessedId, // Add this dependency
    updateHwVersionBatch,
    setHwVersionUpdateProgress,
    resetHwVersionProgress,
  ]);

  const handleUpdateHWVersion = useCallback(() => {
    // Hide the stale recordings table when updating hw version
    setEnabled(false);
    setFeedbackMessage(null);
    setIsUpdatingHWVersion(true);

    // Initialize progress - this will trigger the useEffect
    setHwVersionUpdateProgress({
      totalUpdated: 0,
      currentPage: 1,
      isInProgress: true,
      progressPercentage: 0,
      lastProcessedId: 0, // Start from the beginning
    });
  }, [setHwVersionUpdateProgress]);

  return (
    <Box
      display="grid"
      gridTemplateColumns="2fr 5fr"
      gap={4}
      height="100%"
      overflow="hidden"
    >
      <RecordingManagementConsole
        handleFetchStale={handleFetchStale}
        handleFetchFailed={handleFetchFailed}
        handleUpdateHWVersion={() => {
          handleUpdateHWVersion();
        }}
        handleLoadHWMapping={() => {
          handleLoadHWMapping();
        }}
        isLoading={isLoading || isRefetching}
      />

      {enabled ? (
        <RecordingManagementStaleList
          data={data}
          osfStatus={osfStatus}
          meta={{ totalCount: totalCount ?? 0 }}
          isRefetching={isRefetching}
          isLoading={isLoading}
          pagination={pagination}
          setPagination={setPagination}
          refetch={handleRefresh} // Pass the refresh handler
          reprocessedIds={reprocessedIds} // Pass down the state
          setReprocessedIds={setReprocessedIds}
        />
      ) : (
        <Box display="grid" placeContent="center" justifyItems="center" gap={8}>
          {isUpdatingHWVersion ? (
            <>
              <Box as="p" fontSize="xl">
                Scan Database and update drives HW version. Please wait...
              </Box>
              <Box as="p" fontSize="lg">
                Progress: {hwVersionUpdateProgress.totalUpdated} drives have
                been updated
              </Box>
              <CircularProgress
                value={hwVersionUpdateProgress.progressPercentage}
                size={20}
              />
            </>
          ) : (
            feedbackMessage
          )}
        </Box>
      )}
    </Box>
  );
}
